import { useTranslation } from 'react-i18next';
import { motion, type Variants } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';

// =====================================================================
// AboutLenderlyPage — public colophon for Lexta's sponsor.
//
// Reachable from the footer's "Lexta is operated in partnership with
// Lenderly · Læs mere" stripe and (in a sibling milestone) from the
// filing-receipt curiosity hook. Intentionally calm: this is a register-
// office "about the sponsor" notice, not a marketing landing page.
//
// Editorial tone is the Danish bureaucratic register voice — same as
// the rest of Lexta. No exclamation marks, no checklist of features,
// no emojis. The "Prøv Lenderly" button leaves the site (external <a>
// to lenderly.eu in a new tab). A secondary "request a conversation"
// mailto is temporarily hidden — see the note in the CTA section.
// =====================================================================

const EASE = [0.2, 0, 0, 1] as const;

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: EASE } },
};

const stagger: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};

export default function AboutLenderlyPage() {
  const { t } = useTranslation();

  return (
    <div className="mx-auto max-w-[760px]">
      {/* ============================================================
          1. HEADER — eyebrow, h1, lede
          ============================================================ */}
      <motion.section
        variants={stagger}
        initial="hidden"
        animate="visible"
        className="pt-2 pb-10 sm:pt-4 sm:pb-14"
      >
        <motion.div variants={fadeUp} className="mb-5">
          <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--color-primary-strong)]">
            {t('aboutLenderly.eyebrow')}
          </span>
        </motion.div>
        <motion.h1
          variants={fadeUp}
          className="font-mono text-[clamp(2rem,4vw,3rem)] leading-[1.05] tracking-[-0.02em] text-[var(--color-ink)]"
        >
          {t('aboutLenderly.title')}
        </motion.h1>
        <motion.p
          variants={fadeUp}
          className="mt-6 text-[var(--color-ink-soft)] leading-[1.7] text-[16px]"
        >
          {t('aboutLenderly.lede')}
        </motion.p>
      </motion.section>

      {/* ============================================================
          2. WHAT IS LENDERLY
          ============================================================ */}
      <motion.section
        variants={stagger}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-100px' }}
        className="py-10 sm:py-12 border-t border-[var(--color-rule)]"
      >
        <motion.h2
          variants={fadeUp}
          className="font-mono text-[clamp(1.4rem,2.4vw,1.85rem)] leading-[1.15] tracking-tight text-[var(--color-ink)] mb-6"
        >
          {t('aboutLenderly.whatTitle')}
        </motion.h2>
        <motion.p
          variants={fadeUp}
          className="text-[var(--color-ink-soft)] leading-[1.7] text-[15.5px]"
        >
          {t('aboutLenderly.whatBody1')}
        </motion.p>
        <motion.p
          variants={fadeUp}
          className="mt-4 text-[var(--color-ink-soft)] leading-[1.7] text-[15.5px]"
        >
          {t('aboutLenderly.whatBody2')}
        </motion.p>
      </motion.section>

      {/* ============================================================
          3. WHY SPONSOR
          ============================================================ */}
      <motion.section
        variants={stagger}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-100px' }}
        className="py-10 sm:py-12 border-t border-[var(--color-rule)]"
      >
        <motion.h2
          variants={fadeUp}
          className="font-mono text-[clamp(1.4rem,2.4vw,1.85rem)] leading-[1.15] tracking-tight text-[var(--color-ink)] mb-6"
        >
          {t('aboutLenderly.whyTitle')}
        </motion.h2>
        <motion.p
          variants={fadeUp}
          className="text-[var(--color-ink-soft)] leading-[1.7] text-[15.5px]"
        >
          {t('aboutLenderly.whyBody1')}
        </motion.p>
        <motion.p
          variants={fadeUp}
          className="mt-4 text-[var(--color-ink-soft)] leading-[1.7] text-[15.5px]"
        >
          {t('aboutLenderly.whyBody2')}
        </motion.p>
      </motion.section>

      {/* ============================================================
          4. FINAL CTA — heavier surface, two actions
          ============================================================ */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.5, ease: EASE }}
        className="my-12 sm:my-16"
      >
        <div className="relative overflow-hidden bg-[var(--color-blue-deep)] text-white px-5 sm:px-8 lg:px-12 py-10 sm:py-12 lg:py-14">
          {/* faint decorative wash, same vocabulary as HomePage's CTA */}
          <div
            aria-hidden
            className="absolute -right-16 -top-16 w-[320px] h-[320px] rounded-full bg-[var(--color-primary)]/25 blur-3xl pointer-events-none"
          />
          <div
            aria-hidden
            className="absolute -left-24 -bottom-24 w-[260px] h-[260px] rounded-full bg-white/5 blur-3xl pointer-events-none"
          />

          <div className="relative">
            <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/60">
              {t('aboutLenderly.eyebrow')}
            </span>
            <h2 className="mt-3 font-mono text-[clamp(1.6rem,3vw,2.25rem)] leading-[1.1] tracking-tight">
              {t('aboutLenderly.ctaTitle')}
            </h2>
            <p className="mt-5 max-w-[520px] text-white/80 leading-[1.7] text-[15.5px]">
              {t('aboutLenderly.ctaBody')}
            </p>

            <div className="mt-8 flex items-center gap-3 flex-wrap">
              {/*
                External link to the Lenderly product. New tab — Lexta
                visitors keep their tab on the register, and Lenderly
                opens fresh. ``rel=noopener noreferrer`` per the
                standard external-link hygiene rule.
              */}
              <a
                href="https://lenderly.eu"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 w-full sm:w-auto bg-white text-[var(--color-blue-deep)] hover:bg-[var(--color-paper)] px-7 py-3.5 text-[15px] font-medium transition-colors group"
              >
                {t('aboutLenderly.ctaPrimary')}
                <ArrowUpRight
                  className="w-4 h-4 transition-transform duration-200 ease-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                  strokeWidth={2}
                />
              </a>
              {/*
                Secondary "request a conversation" mailto is hidden for
                now (2026-09-08): the mailbox it pointed at sits on a
                domain with no MX record, so the mail went nowhere.
                Restore once Lenderly has a working contact address —
                the ``aboutLenderly.ctaSecondary`` string is still in
                both locale files.
              */}
            </div>
          </div>
        </div>
      </motion.section>
    </div>
  );
}
